import {useEffect, useRef, useState} from "react";
import humanizeDuration from "humanize-duration";

import {API_BASE_URL} from "../config.js";

const SIMULATOR_INFO = {
    "catlab": {
        name: "Catlab Hardware",
        description: "Remotely interact with the hardware found in DTU Physics Catlab.",
        job_types: ["redpitaya"]
    },
    "tn-sampling": {
        name: "Tensor Network Sampling",
        description: "Performs covariance matrix generation and sampling using tensor networks.",
        job_types: ["tn-covariance", "tn-sampling"]
    },
};

export default function Simulators() {
    const [simulators, setSimulators] = useState([]);
    const ref = useRef(null);

    const fetchSimulators = async() => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/status`, {
                method: "GET",
            });

            if(!res.ok) {
                throw new Error("Failed to fetch simulators");
            };

            const data = await res.json();
            setSimulators(data ?? []);
        }catch(e) {
            console.error("Failed to fetch simulators");
        };
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            if(document.visibilityState === "visible") {
                fetchSimulators();
                ref.current = setInterval(fetchSimulators, 60 * 1000);
            }else {
                clearInterval(ref.current);
            };
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        if(document.visibilityState === "visible") {
            fetchSimulators();
            ref.current = setInterval(fetchSimulators, 60 * 1000);
        };

        return () => {
            clearInterval(ref.current);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    const onlineSimulatorsById = Object.fromEntries(simulators.map(sim => [sim.worker_id, sim]));

    return (
        <div>
            {Object.entries(SIMULATOR_INFO).map(([worker_id, info]) => {
                const simulator = onlineSimulatorsById[worker_id] ?? {worker_id, last_seen: null};
                const status = simulator.status ?? "offline";
                const isOnline = status === "online";

                const lastSeenLabel = simulator.last_seen ? humanizeDuration(Date.now() - simulator.last_seen, {largest: 1, round: true}) + " ago" : "Offline";
                const uptimeLabel = simulator.uptime ? humanizeDuration(simulator.uptime, {largest: 2, round: true}) : null;

                return (
                    <div className="simulator" key={worker_id}>
                        <div className="simulator-header">
                            <h3>{info.name}</h3>
                            <span className={`status ${status}`} title={`Last seen: ${lastSeenLabel}${uptimeLabel ? `\nUptime: ${uptimeLabel}`: ""}`}>{isOnline ? "Online" : "Offline"}</span>
                        </div>
                        <div className="simulator-info">
                            <p>{info.description}</p>
                            <div className="job-types">
                                Available job types:{" "}
                                {info.job_types.map((v, i) => (
                                    <span key={v}>
                                        <code>{v}</code>
                                        {i < info.job_types.length - 1 && ", "}
                                    </span>
                                ))}
                            </div>
                            <span className="queue-length">Jobs in queue: {simulator.queue_length || 0}</span>
                        </div>
                    </div>
                )
            })}
        </div>
    );
};
